import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AccountService, AlertService } from '@app/_services';
import { ProductMasterService, ProductMasterReport } from './product-master.service';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { ColDef, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { first } from 'rxjs/operators';

@Component({
  selector: 'app-product-master',
  templateUrl: './product-master.component.html',
  styleUrls: ['./product-master.component.less'],
  standalone: true,
  imports: [CommonModule, AgGridModule]
})
export class ProductMasterComponent implements OnInit {
  @ViewChild('agGrid', { static: false }) agGrid: AgGridAngular;

  plans: ProductMasterReport[] = [];
  filteredPlans: ProductMasterReport[] = [];
  loading = false;
  user: any;
  searchTerm = '';
  pageSizeOptions = [5, 10, 20, 50];
  gridOptions: GridOptions = {
    domLayout: 'autoHeight',
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [5, 10, 20, 50],
    suppressHorizontalScroll: false,
    columnHoverHighlight: true,
    rowHeight: 42,
    headerHeight: 46,
    animateRows: true,
    suppressMenuHide: false,
    suppressMovableColumns: false,
    suppressPaginationPanel: false,
    paginationAutoPageSize: false,
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: false
    },
    localeText: {
      page: 'Page',
      more: 'More',
      to: 'to',
      of: 'of',
      next: '▶',
      last: '⏭',
      first: '⏮',
      previous: '◀'
    }
  };
  columnDefs: ColDef[] = [];

  constructor(
    private productMasterService: ProductMasterService,
    private accountService: AccountService,
    private alertService: AlertService
  ) {
    this.user = this.accountService.userValue;
  }

  ngOnInit(): void {
    // Authorization check
    if (this.user.role !== 'admin' && this.user.role !== 'heroEmployee') {
      this.alertService.error('Unauthorized: Access to Product Master report is restricted.');
      return;
    }

    this.loadPlans();
    this.initializeGridColumns();
  }

  private loadPlans(): void {
    this.loading = true;
    this.productMasterService.getProductMasterPlans()
      .pipe(first())
      .subscribe({
        next: (data) => {
          const plans = this.user.role === 'heroEmployee'
            ? data.filter((plan) => this.isHeroPlan(plan))
            : data;

          this.plans = plans;
          this.filteredPlans = plans;
          this.loading = false;
          // Refresh grid after data loads
          if (this.agGrid?.api) {
            this.agGrid.api.setRowData(this.filteredPlans);
          }
        },
        error: (error) => {
          this.alertService.error('Failed to load product master data: ' + error);
          this.loading = false;
        }
      });
  }

  onSearchChange(event: any): void {
    this.searchTerm = event.target.value.toLowerCase();
    this.applyFilter();
  }

  private applyFilter(): void {
    if (!this.searchTerm) {
      this.filteredPlans = [...this.plans];
    } else {
      this.filteredPlans = this.plans.filter(plan =>
        plan.planId?.toLowerCase().includes(this.searchTerm) ||
        plan.policyType?.toLowerCase().includes(this.searchTerm)
      );
    }

    if (this.agGrid?.api) {
      this.agGrid.api.setRowData(this.filteredPlans);
    }
  }

  private initializeGridColumns(): void {
    const baseColumns: ColDef[] = [
      {
        headerName: 'Plan ID',
        field: 'planId',
        sortable: true,
        filter: true,
        width: 140
      }
    ];

    // Financial columns with remapped fields
    const financialColumns: ColDef[] = [
      {
        headerName: 'Plan Amount',
        valueGetter: (params) => {
          const planTotalAmount = params.data.planTotalAmount;
          if (planTotalAmount === 0) {
            const minAmount = params.data.planMinimumAmount;
            const maxAmount = params.data.planMaximumAmount;

            if (Number.isFinite(minAmount) && Number.isFinite(maxAmount)) {
              return `${Math.round(minAmount)}-${Math.round(maxAmount)}`;
            }

            return 'Variable';
          }

          return planTotalAmount;
        },
        valueFormatter: (params) => this.roundedValueFormatter(params),
        sortable: true,
        filter: true,
        width: 130
      },
      {
        headerName: 'Pre GST',
        valueGetter: (params) => {
          const planAmount = params.data.planAmount;
          return planAmount > 0 ? planAmount : 'Variable';
        },
        valueFormatter: (params) => this.roundedValueFormatter(params),
        sortable: true,
        filter: true,
        width: 120
      },
      {
        headerName: 'GST',
        valueGetter: (params) => {
          const planGstAmount = params.data.planGstAmount;
          return planGstAmount > 0 ? planGstAmount : 'Variable';
        },
        valueFormatter: (params) => this.roundedValueFormatter(params),
        sortable: true,
        filter: true,
        width: 120
      },
      {
        headerName: 'DLR Margin',
        valueGetter: (params) => {
          const planAmount = params.data.planAmount;
          if (planAmount > 0) {
            return planAmount - params.data.planNetAmount;
          }
          return 'Variable';
        },
        valueFormatter: (params) => this.roundedValueFormatter(params),
        sortable: true,
        filter: true,
        width: 120
      },
      {
        headerName: 'Cost',
        valueGetter: (params) => params.data.planNetAmount,
        valueFormatter: (params) => this.roundedValueFormatter(params),
        sortable: true,
        filter: true,
        width: 120,
        cellClass: 'financial-highlight'
      },
      {
        headerName: 'Product Rack Rate',
        field: 'productRackRate',
        valueFormatter: (params) => this.roundedValueFormatter(params),
        sortable: true,
        filter: true,
        width: 160,
        cellClass: 'financial-highlight'
      },
      {
        headerName: 'Operating Cost',
        valueGetter: (params) => params.data.operatingCost ?? 0,
        valueFormatter: (params) => this.roundedValueFormatter(params),
        sortable: true,
        filter: true,
        width: 150,
        cellClass: 'financial-highlight'
      },
      {
        headerName: 'Retention Cost',
        field: 'retentionCost',
        valueFormatter: (params) => this.roundedValueFormatter(params),
        sortable: true,
        filter: true,
        width: 150,
        cellClass: 'financial-highlight'
      },
      {
        headerName: 'Net Benefit',
        field: 'netBenefit',
        valueFormatter: (params) => this.roundedValueFormatter(params),
        sortable: true,
        filter: true,
        width: 130,
        cellClass: 'financial-highlight'
      }
    ];

    // Check for superAdmin access
    const isSuperAdmin = this.user.specialRoleAccess &&
      this.user.specialRoleAccess.includes('HeroSuperAdmin');

    // Check for manager access
    const isManager = this.user.specialRoleAccess &&
      this.user.specialRoleAccess.includes('HeroManager');

    // Check for executive access
    const isExecutive = this.user.specialRoleAccess &&
      this.user.specialRoleAccess.includes('HeroExecutive');

    // Admin has SuperAdmin view (all columns)
    const isAdmin = this.user.role === 'admin';

    if (isAdmin || isSuperAdmin) {
      // Admin / HeroSuperAdmin: all columns
      this.columnDefs = [...baseColumns, ...financialColumns];
    } else if (isManager) {
      // HeroManager: hide Product Rack Rate, Operating Cost, Retention Cost, Net Benefit
      this.columnDefs = [...baseColumns, ...financialColumns.slice(0, 5)];
    } else if (isExecutive) {
      // HeroExecutive: Plan ID, Plan Amount, Pre GST, GST, Net Benefit
      this.columnDefs = [
        ...baseColumns,
        financialColumns[0],
        financialColumns[1],
        financialColumns[2],
        financialColumns[8]
      ];
    }

    // Default for no special access (should not reach here if properly authorized)
    if (this.columnDefs.length === 0) {
      this.columnDefs = baseColumns;
    }
  }

  onGridReady(params: any): void {
    params.api.sizeColumnsToFit();
  }

  private roundedValueFormatter(params: ValueFormatterParams): string {
    const value = params.value;

    if (value == null || value === '') {
      return '';
    }

    if (typeof value === 'string') {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? Math.round(numericValue).toString() : value;
    }

    if (typeof value === 'number') {
      return Math.round(value).toString();
    }

    return String(value);
  }

  onPageSizeChange(event: any): void {
    const pageSize = parseInt(event.target.value, 10);
    this.agGrid?.api?.setGridOption('paginationPageSize', pageSize);
  }

  refreshData(): void {
    this.loadPlans();
  }

  exportToCSV(): void {
    if (this.agGrid && this.agGrid.api) {
      this.agGrid.api.exportDataAsCsv({
        fileName: `ProductMaster_${new Date().toISOString().split('T')[0]}.csv`
      });
    }
  }

  private isHeroPlan(plan: ProductMasterReport): boolean {
    const planType = plan?.planType?.toLowerCase();
    const planId = plan?.planId?.toUpperCase();

    return planType === 'hero ensure' || !!planId?.startsWith('AA');
  }
}
